import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Plus, Trash2 } from 'lucide-react';

const HighlightsEditor = () => {
    const [title, setTitle] = useState('');
    const [highlights, setHighlights] = useState([]);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState('');

    useEffect(() => {
        const fetchContent = async () => {
            try {
                const { data } = await api.get('/sections/Highlights');
                if (data && data.content) {
                    const contentMap = {};
                    data.content.forEach(item => contentMap[item.key] = item.value);

                    if (contentMap.title) setTitle(contentMap.title);
                    if (contentMap.items) {
                        try {
                            setHighlights(JSON.parse(contentMap.items));
                        } catch (e) {
                            console.error('Error parsing items', e);
                        }
                    }
                }
                setLoading(false);
            } catch (error) {
                console.error(error);
                setLoading(false);
            }
        };
        fetchContent();
    }, []);

    const handleAddHighlight = () => {
        setHighlights([...highlights, {
            id: Date.now(),
            name: "New Item",
            role: "Description",
            textLists: ["New Highlight"],
            video: "",
            videoDuration: 4
        }]);
    };

    const handleRemoveHighlight = (id) => {
        setHighlights(highlights.filter(h => h.id !== id));
    };

    const handleHighlightChange = (id, field, value) => {
        setHighlights(highlights.map(h => h.id === id ? { ...h, [field]: value } : h));
    };

    const handleTextListChange = (id, index, value) => {
        setHighlights(highlights.map(h => {
            if (h.id === id) {
                const newLists = [...(h.textLists || [])];
                newLists[index] = value;
                return { ...h, textLists: newLists };
            }
            return h;
        }));
    };

    const handleVideoUpload = async (id, file) => {
        if (!file) return;
        const formData = new FormData();
        formData.append('file', file);
        try {
            const { data } = await api.post('/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            handleHighlightChange(id, 'video', data.path); // Save path
        } catch (error) {
            console.error("Upload failed", error);
            setMessage("Video upload failed");
        }
    };

    const handleSave = async () => {
        try {
            const contentArray = [
                { key: 'title', type: 'text', value: title },
                { key: 'items', type: 'json', value: JSON.stringify(highlights) }
            ];
            await api.put('/sections/Highlights', { content: contentArray });
            setMessage('Highlights updated successfully!');
            setTimeout(() => setMessage(''), 3000);
        } catch (error) {
            setMessage('Error saving highlights');
        }
    };

    if (loading) return <div>Loading...</div>;

    return (
        <div className="max-w-4xl mx-auto bg-white p-8 rounded shadow text-gray-900">
            <h2 className="text-2xl font-bold mb-6">Edit Highlights Section</h2>
            {message && <div className={`p-4 mb-4 rounded ${message.includes('Error') ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>{message}</div>}

            <div className="space-y-6">
                <div>
                    <label className="block text-sm font-medium text-gray-700">Section Title</label>
                    <input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        className="w-full mt-1 p-2 border rounded"
                    />
                </div>

                <div>
                    <div className="flex justify-between items-center mb-2">
                        <label className="block text-sm font-medium text-gray-700">Highlight Items</label>
                        <button onClick={handleAddHighlight} type="button" className="flex items-center text-sm text-blue-600 hover:text-blue-800">
                            <Plus className="w-4 h-4 mr-1" /> Add Carousel Item
                        </button>
                    </div>

                    <div className="space-y-4">
                        {highlights.map((item, index) => (
                            <div key={item.id} className="border p-4 rounded bg-gray-50 relative group">
                                <button
                                    onClick={() => handleRemoveHighlight(item.id)}
                                    className="absolute top-2 right-2 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                                <div className="grid grid-cols-1 gap-4">
                                    {/* Name and Role */}
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-xs text-gray-500">Name / Title</label>
                                            <input
                                                type="text"
                                                value={item.name || ""}
                                                onChange={(e) => handleHighlightChange(item.id, 'name', e.target.value)}
                                                className="w-full mt-1 p-2 border rounded text-sm"
                                                placeholder="e.g. SNC AI Innovation"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-500">Role / Subtitle</label>
                                            <input
                                                type="text"
                                                value={item.role || ""}
                                                onChange={(e) => handleHighlightChange(item.id, 'role', e.target.value)}
                                                className="w-full mt-1 p-2 border rounded text-sm"
                                                placeholder="e.g. Experience the future"
                                            />
                                        </div>
                                    </div>

                                    {/* Text Lists */}
                                    <div>
                                        <label className="block text-xs text-gray-500">Caption Lines (Labels)</label>
                                        {(item.textLists || [""]).map((text, i) => (
                                            <input
                                                key={i}
                                                type="text"
                                                value={text}
                                                onChange={(e) => handleTextListChange(item.id, i, e.target.value)}
                                                className="w-full mt-1 p-2 border rounded text-sm mb-1"
                                                placeholder={`Line ${i + 1}`}
                                            />
                                        ))}
                                        <button
                                            type="button"
                                            className="text-xs text-blue-500 underline"
                                            onClick={() => {
                                                const newLists = [...(item.textLists || []), ""];
                                                handleHighlightChange(item.id, 'textLists', newLists);
                                            }}
                                        >
                                            + Add Label
                                        </button>
                                    </div>

                                    {/* Video Upload */}
                                    <div>
                                        <label className="block text-xs text-gray-500">Video</label>
                                        {item.video && (
                                            <div className="text-xs text-green-600 truncate mb-1">
                                                path: {item.video}
                                            </div>
                                        )}
                                        <input
                                            type="file"
                                            accept="video/*"
                                            onChange={(e) => handleVideoUpload(item.id, e.target.files[0])}
                                            className="block w-full text-sm text-gray-500 file:mr-4 file:py-1 file:px-2 file:rounded-full file:border-0 file:text-xs file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-xs text-gray-500">Duration (seconds)</label>
                                        <input
                                            type="number"
                                            value={item.videoDuration || 4}
                                            onChange={(e) => handleHighlightChange(item.id, 'videoDuration', parseFloat(e.target.value))}
                                            className="w-24 mt-1 p-2 border rounded text-sm"
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <button onClick={handleSave} className="px-6 py-2 bg-black text-white rounded hover:bg-gray-800">
                    Save Changes
                </button>
            </div>
        </div>
    );
};

export default HighlightsEditor;
